import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X, Package } from "lucide-react";

const CATEGORIES = ["mixed", "estate", "household", "tools", "commercial", "collectibles"];
const UNIT_SIZES = ["5x5", "5x10", "10x10", "10x15", "10x20", "10x30", "20x20", "20x30"];

export default function SellPage() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    facility_name: "",
    city: "",
    state: "",
    zip_code: "",
    unit_size: "10x10",
    unit_number: "",
    category: "mixed",
    starting_bid: "50",
    bid_increment: "10",
    buyer_premium_pct: "8",
    duration_hours: "72",
    auto_extend_minutes: "5",
    custom_rules: "",
    content_hints: "",
  });
  const [photos, setPhotos] = useState([]); // { path, preview }
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFiles = async (files) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { path: data.path, preview: URL.createObjectURL(file) }]);
      } catch (e) {
        toast.error(formatError(e));
      }
    }
    setUploading(false);
  };

  const removePhoto = (idx) => {
    setPhotos((p) => p.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        starting_bid: parseFloat(form.starting_bid),
        bid_increment: parseFloat(form.bid_increment),
        buyer_premium_pct: parseFloat(form.buyer_premium_pct),
        duration_hours: parseInt(form.duration_hours, 10),
        auto_extend_minutes: parseInt(form.auto_extend_minutes, 10),
        custom_rules: form.custom_rules || "",
        content_hints: form.content_hints || "",
        photos: photos.map((p) => p.path),
      };
      const { data } = await api.post("/auctions", payload);
      if (data._promo_bonus_awarded) {
        toast.success(`Auction created — $${data._promo_bonus_awarded} promo credit earned!`);
      } else {
        toast.success("Auction created!");
      }
      nav(`/auctions/${data.id}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">List a Unit</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
          Create Auction
        </h1>
        <p className="text-sm text-zinc-500 mt-3 max-w-2xl">
          Post an abandoned storage unit for public auction. Photos and honest descriptions drive bids.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-8">
        <Section title="Listing Details">
          <FormField label="Title (headline)">
            <Input data-testid="sell-title" required maxLength={120} value={form.title} onChange={set("title")} placeholder="e.g., 10x20 Mystery Unit — Estate Contents" className={inputCls} />
          </FormField>
          <FormField label="Description">
            <Textarea data-testid="sell-description" required rows={5} value={form.description} onChange={set("description")} placeholder="Describe what you see through the door. Be honest — buyers reward transparency." className={inputCls} />
          </FormField>
          <FormField label="Content Hints (optional)">
            <p className="text-xs text-zinc-500 leading-relaxed -mt-1 mb-2">
              A single-line observation to help bidders read the photos. Keep it general — don&apos;t itemize or guarantee contents. This protects you legally.
            </p>
            <Input
              data-testid="sell-content-hints"
              value={form.content_hints}
              onChange={set("content_hints")}
              maxLength={200}
              placeholder="e.g., Furniture visible in back • Boxes stacked to ceiling • Long-term unit"
              className={inputCls}
            />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Category">
              <Select value={form.category} onValueChange={setSel("category")}>
                <SelectTrigger data-testid="sell-category" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Unit Size">
              <Select value={form.unit_size} onValueChange={setSel("unit_size")}>
                <SelectTrigger data-testid="sell-unit-size" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                  {UNIT_SIZES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </div>
        </Section>

        <Section title="Facility & Location">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Facility Name">
              <Input data-testid="sell-facility" required value={form.facility_name} onChange={set("facility_name")} className={inputCls} />
            </FormField>
            <FormField label="Unit Number">
              <Input data-testid="sell-unit-number" value={form.unit_number} onChange={set("unit_number")} className={inputCls} placeholder="Optional" />
            </FormField>
            <FormField label="City">
              <Input data-testid="sell-city" required value={form.city} onChange={set("city")} className={inputCls} />
            </FormField>
            <FormField label="State (2-letter)">
              <Input data-testid="sell-state" required maxLength={2} value={form.state} onChange={set("state")} className={inputCls + " uppercase"} />
            </FormField>
            <FormField label="ZIP Code">
              <Input data-testid="sell-zip" required value={form.zip_code} onChange={set("zip_code")} className={inputCls} />
            </FormField>
          </div>
        </Section>

        <Section title="Photos">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {photos.map((p, i) => (
              <div key={p.preview || `photo-${i}`} className="relative aspect-square bg-zinc-950 border border-zinc-800 overflow-hidden group">
                <img src={p.preview} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 border border-zinc-700 hover:border-red-500 p-1 opacity-0 group-hover:opacity-100 transition">
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="sell-upload-photos"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="aspect-square bg-zinc-950 border border-dashed border-zinc-700 hover:border-orange-500 hover:text-orange-500 flex flex-col items-center justify-center gap-2 text-zinc-500 text-xs uppercase tracking-widest"
            >
              <Upload className="w-5 h-5" />
              {uploading ? "Uploading…" : "Add Photos"}
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && uploadFiles(e.target.files)}
          />
        </Section>

        <Section title="Auction Terms">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <FormField label="Starting Bid ($)">
              <Input data-testid="sell-starting-bid" type="number" required min="1" step="0.01" value={form.starting_bid} onChange={set("starting_bid")} className={inputCls} />
            </FormField>
            <FormField label="Bid Increment ($)">
              <Input data-testid="sell-bid-increment" type="number" required min="1" step="0.01" value={form.bid_increment} onChange={set("bid_increment")} className={inputCls} />
            </FormField>
            <FormField label="Buyer's Premium (%)">
              <Input data-testid="sell-premium" type="number" required min="0" max="50" step="0.5" value={form.buyer_premium_pct} onChange={set("buyer_premium_pct")} className={inputCls} />
            </FormField>
            <FormField label="Duration (hours)">
              <Input data-testid="sell-duration" type="number" required min="1" max="720" value={form.duration_hours} onChange={set("duration_hours")} className={inputCls} />
            </FormField>
            <FormField label="Auto-Extend (minutes)">
              <Input data-testid="sell-auto-extend" type="number" required min="0" max="60" value={form.auto_extend_minutes} onChange={set("auto_extend_minutes")} className={inputCls} />
            </FormField>
          </div>
        </Section>

        <Section title="Facility-Specific Rules (Optional)">
          <p className="text-xs text-zinc-500 leading-relaxed -mt-2 mb-3">
            Bidders must accept the standard 11 platform rules (payment terms, 48-hour pickup, personal-items return, no hazmat, etc.) — these are automatic. Use this field only to add rules specific to your facility (e.g., pickup hours, deposit amount, access restrictions).
          </p>
          <FormField label="Custom rules (leave blank if none)">
            <Textarea
              data-testid="sell-custom-rules"
              rows={5}
              value={form.custom_rules}
              onChange={set("custom_rules")}
              placeholder={"e.g.,\n• Pickup available Mon-Fri 9am-5pm only\n• $100 refundable clean-out deposit required at pickup\n• Cash only for the buyer's premium at facility\n• 4-hour advance notice required for pickup"}
              className={inputCls}
              maxLength={4000}
            />
          </FormField>
        </Section>

        <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
          <div className="text-xs text-zinc-500 max-w-sm">
            By publishing, you confirm you have legal right to auction the contents of this unit under state lien-holder statutes.
          </div>
          <Button
            type="submit"
            data-testid="sell-submit"
            disabled={submitting}
            className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 px-8 text-sm uppercase tracking-widest font-semibold"
          >
            <Package className="w-4 h-4 mr-2" />
            {submitting ? "Publishing…" : "Publish Auction"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "rounded-none bg-zinc-950 border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input";

function Section({ title, children }) {
  return (
    <div className="bg-zinc-950/50 border border-zinc-800 p-6">
      <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-5">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
